import { useEffect, useState } from "react";

import styles from "./styles.module.scss";
import { ResourcesDataGridReport } from "../components/ResourcesDataGrid";
import { calculateTimeRange } from "../../../utils/timeUtils";
import {
  IReportMetricsItem,
  IReportMetricsRow,
  IReportMetricsTotal,
} from "../../../@types/reports-metrics.type";
import {
  getResourcesMetrics,
  getResourcesMetricsByPositionId,
  getResourcesMetricsByProjectId,
} from "../../../services/api/resource/resourceService";
import { SelectedType } from "./../index";
import { DefaultPaperReport } from "../components/DefaultPaper";
import { DefaultDataGridReport } from "../components/DefaultDataGrid";
import { BarChart } from "../components/Charts/BarChart";
import { CircleChart } from "../components/Charts/CircleChart";
import { PieChart } from "../components/Charts/PieChart";
import CircularProgress from "@mui/material/CircularProgress";

const createInitialMetricsTotal = (): IReportMetricsTotal => {
  return {
    totalScheduled: "-",
    totalBillable: "-",
    totalNonBillable: "-",
    totalScheduledPercentage: 0,
    totalBillablePercentage: 0,
    totalNonBillablePercentage: 0,
    totalNonScheduled: "-",
    totalAvailable: "-",
    totalUnavailability: "-",
    unavailabilities: [],
  };
};

export const ReportResources: React.FC<{
  filterState: any;
  rowSelected: IReportMetricsRow | null;
  selectedType: SelectedType | null;
  setRowSelected: React.Dispatch<
    React.SetStateAction<IReportMetricsRow | null>
  >;
  setSelectedType: React.Dispatch<React.SetStateAction<SelectedType | null>>;
  setValueTab: React.Dispatch<React.SetStateAction<number>>;
  valueTab: number;
}> = ({
  filterState,
  rowSelected = null,
  selectedType = null,
  setRowSelected,
  setSelectedType,
  setValueTab,
  valueTab,
}) => {
    const [resourcesMetrics, setResourcesMetrics] = useState<
      IReportMetricsItem[]
    >([]);
    const [resourcesMetricsRows, setResourcesMetricsRows] = useState<
      IReportMetricsRow[]
    >([]);
    const [resourcesMetricsTotal, setResourcesMetricsTotal] =
      useState<IReportMetricsTotal>(createInitialMetricsTotal);
    const [totalRows, setTotalRows] = useState(0);
    const [loading, setLoading] = useState<boolean>(false);

    async function fetchAllResourcesMetrics() {
      try {
        setLoading(true);
        const { startDate, endDate } = calculateTimeRange(
          filterState.currentPeriod,
          filterState.period
        );
        const startTime = startDate.startOf("day").toISOString();
        const endTime = endDate.startOf("day").toISOString();
        let response;
        if (rowSelected) {
          const paramsMetrics = {
            id: rowSelected?.id,
            startTime: startTime,
            endTime: endTime,
            timeType: filterState.timeMode,
          };
          switch (selectedType) {
            case "project":
              response = await getResourcesMetricsByProjectId(paramsMetrics);
              break;
            case "position":
              response = await getResourcesMetricsByPositionId(paramsMetrics);
              break;
            default:
              response = await getResourcesMetrics({
                startTime: startTime,
                endTime: endTime,
                timeType: filterState.timeMode,
              });
              break;
          }
        } else {
          response = await getResourcesMetrics({
            startTime: startTime,
            endTime: endTime,
            timeType: filterState.timeMode,
          });
          setSelectedType("resource");
        }

        setTotalRows(response.result.metric.length);
        setResourcesMetrics(response.result.metric);
        setResourcesMetricsTotal({
          totalScheduled:
            filterState.timeMode === 1
              ? `${response.result.totalScheduled.toLocaleString("pt-BR") || 0}h`
              : `${response.result.totalScheduled.toLocaleString("pt-BR") || 0}d`,
          totalBillable:
            filterState.timeMode === 1
              ? `${response.result.totalBillable.toLocaleString("pt-BR") || 0}h`
              : `${response.result.totalBillable.toLocaleString("pt-BR") || 0}d`,
          totalNonBillable:
            filterState.timeMode === 1
              ? `${response.result.totalNonBillable.toLocaleString("pt-BR") || 0
              }h`
              : `${response.result.totalNonBillable.toLocaleString("pt-BR") || 0
              }d`,
          totalNonScheduled:
            filterState.timeMode === 1
              ? `${response.result.totalNonScheduled.toLocaleString("pt-BR") || 0
              }h`
              : `${response.result.totalNonScheduled.toLocaleString("pt-BR") || 0
              }d`,
          totalAvailable:
            filterState.timeMode === 1
              ? `${response.result.totalAvailable.toLocaleString("pt-BR") || 0}h`
              : `${response.result.totalAvailable.toLocaleString("pt-BR") || 0}d`,
          totalGeneral:
            filterState.timeMode === 1
              ? `${response.result.totalGeneral.toLocaleString("pt-BR") || 0}h`
              : `${response.result.totalGeneral.toLocaleString("pt-BR") || 0}d`,
          totalScheduledPercentage:
            response.result.totalScheduledPercentage != null
              ? Number(response.result.totalScheduledPercentage.toFixed(2))
              : 0,
          totalBillablePercentage:
            response.result.totalBillablePercentage != null
              ? Number(response.result.totalBillablePercentage.toFixed(2))
              : 0,
          totalNonBillablePercentage:
            response.result.totalNonBillablePercentage != null
              ? Number(response.result.totalNonBillablePercentage.toFixed(2))
              : 0,
          totalUnavailability:
            filterState.timeMode === 1
              ? `${response.result.totalUnavailability.toLocaleString("pt-BR") || 0
              }h`
              : `${response.result.totalUnavailability.toLocaleString("pt-BR") || 0
              }d`,
          unavailabilities: response.result.unavailabilities,
        });
      } catch (error) {
        console.log(error);
      } finally {
        setLoading(false);
      }
    }

    useEffect(() => {
      fetchAllResourcesMetrics();
    }, [filterState, rowSelected]);

    const transformMetricsToRows = (
      metrics: IReportMetricsItem[]
    ): IReportMetricsRow[] => {
      return metrics.map((item) => ({
        id: item.id,
        name: item.name,
        totalScheduled:
          filterState.timeMode === 1
            ? `${item.scheduled}h`
            : `${item.scheduled}d`,
        billed:
          filterState.timeMode === 1 ? `${item.billable}h` : `${item.billable}d`,
        notBilled:
          filterState.timeMode === 1
            ? `${item.nonBillable}h`
            : `${item.nonBillable}d`,
        notAllocated:
          filterState.timeMode === 1
            ? `${item.nonScheduled}h`
            : `${item.nonScheduled}d`,
        projection:
          filterState.timeMode === 1
            ? `${item.projection}h`
            : `${item.projection}d`,
        percentScheduled: item.scheduledPercentage,
        unavailability:
          filterState.timeMode === 1
            ? `${item.unavailability}h`
            : `${item.unavailability}d`,
      }));
    };

    useEffect(() => {
      const transformedRows = transformMetricsToRows(resourcesMetrics);
      setResourcesMetricsRows(transformedRows);
    }, [resourcesMetrics]);

    return loading ? (
      <div
        style={{
          position: "absolute",
          top: "50%",
          left: "50%",
          transform: "translate(-50%, -50%)",
        }}
      >
        <CircularProgress size={100} />
      </div>
    ) : selectedType === "resource" ? (
      <main className={styles.container}>
        <div className={styles.divGraphs}>
          <BarChart
            totalNonScheduled={resourcesMetricsTotal?.totalNonScheduled}
            totalAvailable={resourcesMetricsTotal?.totalAvailable}
            totalScheduled={resourcesMetricsTotal?.totalScheduled}
            totalScheduledPercentage={
              resourcesMetricsTotal?.totalScheduledPercentage
            }
            totalGeneral={resourcesMetricsTotal?.totalGeneral}

          />

          <CircleChart
            totalBillable={resourcesMetricsTotal?.totalBillable}
            totalBillablePercentage={
              resourcesMetricsTotal?.totalBillablePercentage
            }
            totalNonBillable={resourcesMetricsTotal?.totalNonBillable}
            totalNonBillablePercentage={
              resourcesMetricsTotal?.totalNonBillablePercentage
            }
          />
          <PieChart unavailabilities={resourcesMetricsTotal?.unavailabilities} />
        </div>

        <ResourcesDataGridReport
          setRowSelected={setRowSelected}
          rows={resourcesMetricsRows}
          rowCount={totalRows}
          selectedType={selectedType}
          setValueTab={setValueTab}
          setSelectedType={setSelectedType}
          filterState={filterState}
        />
      </main>
    ) : (
      <>
        <DefaultPaperReport
          scheduledData={String(resourcesMetricsTotal?.totalScheduled)}
          billedData={String(resourcesMetricsTotal?.totalBillable)}
          billedPercentData={resourcesMetricsTotal?.totalBillablePercentage}
          notBilledData={String(resourcesMetricsTotal?.totalNonBillable)}
          notBilledPercentData={resourcesMetricsTotal?.totalNonBillablePercentage}
          filterState={filterState}
        />
        <DefaultDataGridReport
          columnName="Colaboradores"
          rows={resourcesMetricsRows}
          rowCount={totalRows}
          selectedType={selectedType}
          setSelectedType={setSelectedType}
          setRowSelected={setRowSelected}
          setValueTab={setValueTab}
          valueTab={valueTab}
          loading={loading}
          filterState={filterState}
        />
      </>
    );
  };
